import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useFormik } from 'formik';
import * as yup from 'yup';
import {
  Box,
  Button,
  Container,
  TextField,
  Typography,
  Alert,
  Paper,
} from '@mui/material';
import { useLoginMutation, authApi } from '../store/services/auth';
import { setCredentials, logout } from '../store/slices/authSlice';
import { usersApi } from '../store/services/users';
import { shopsApi } from '../store/services/shops';
import { shopCategoriesApi } from '../store/services/shopCategories';
import { productCategoriesApi } from '../store/services/productCategories';
import { productsApi } from '../store/services/products';
import { companiesApi } from '../store/services/companies';

const validationSchema = yup.object({
  username: yup.string().required('Username is required'),
  password: yup.string().required('Password is required'),
});

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const [login] = useLoginMutation();
  const [error, setError] = useState<string | null>(null);

  const from = location.state?.from?.pathname || '/';

  // Clear state on component mount
  useEffect(() => {
    dispatch(logout());
    dispatch(authApi.util.resetApiState());
  }, [dispatch]);

  const formik = useFormik({
    initialValues: {
      username: '',
      password: '',
    },
    validationSchema,
    onSubmit: async (values) => {
      try {
        // Clear any existing session and reset ALL API states
        dispatch(logout());
        
        // Explicitly reset all API states to ensure clean cache
        dispatch(authApi.util.resetApiState());
        dispatch(usersApi.util.resetApiState());
        dispatch(shopsApi.util.resetApiState());
        dispatch(shopCategoriesApi.util.resetApiState());
        dispatch(productCategoriesApi.util.resetApiState());
        dispatch(productsApi.util.resetApiState());
        dispatch(companiesApi.util.resetApiState());
        
        // Attempt login
        const result = await login(values).unwrap();
        
        // Set token in state
        dispatch(setCredentials({ token: result.access_token }));
          // Fetch user data directly using the endpoint
        const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/api/auth/me`, {
          headers: {
            'Authorization': `Bearer ${result.access_token}`,
            'Content-Type': 'application/json'
          }
        });
        
        if (!response.ok) {
          throw new Error('Failed to fetch user data');
        }
        
        const userData = await response.json();
        
        // Update state with both token and user data
        dispatch(setCredentials({ 
          token: result.access_token,
          user: userData 
        }));
        
        navigate(from, { replace: true });
      } catch (err: any) {
        console.error('Login error:', err);
        if (err.data?.detail === 'Your Account is blocked, Contact Admin') {
          setError('Your account is blocked. Please contact the administrator.');
        } else {
          setError('Invalid username or password');
        }
        // Ensure cleanup on error
        dispatch(logout());
      }
    },
  });

  return (
    <Container component="main" maxWidth="xs">
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Paper
          elevation={3}
          sx={{
            p: 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            width: '100%',
          }}
        >
          <Typography component="h1" variant="h5" sx={{ mb: 3 }}>
            OmniServe Admin
          </Typography>
          <form onSubmit={formik.handleSubmit} style={{ width: '100%' }}>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}
            <TextField
              fullWidth
              id="username"
              name="username"
              label="Username"
              value={formik.values.username}
              onChange={formik.handleChange}
              error={formik.touched.username && Boolean(formik.errors.username)}
              helperText={formik.touched.username && formik.errors.username}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              id="password"
              name="password"
              label="Password"
              type="password"
              value={formik.values.password}
              onChange={formik.handleChange}
              error={formik.touched.password && Boolean(formik.errors.password)}
              helperText={formik.touched.password && formik.errors.password}
              sx={{ mb: 3 }}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={formik.isSubmitting}
            >
              {formik.isSubmitting ? 'Signing in...' : 'Sign In'}
            </Button>
          </form>
        </Paper>
      </Box>
    </Container>
  );
}